import { useCallback, useMemo } from "react";
import omit from "lodash/omit";

import FormFieldOptionsSelect from "./FormFieldOptionsSelect";
import OtherFieldForSelect from "./OtherFieldForSelect";
import inputConnect from "./inputConnect";
import Icon from "../Utils/Icon";
import { useFieldContext } from "../Form/Field";

const correctionIconClassName = option =>
  option.isCorrect ? "fa-check text-success" : "fa-times text-danger";

const quizzAnswerLabel = option => (
  <>
    <Icon className={`fa fa-fw ${correctionIconClassName(option)} margin-right-md`} />
    <span>{option.name}</span>
  </>
);

const CheckboxesSelectInput = props => {
  const { fieldId } = useFieldContext();
  const {
    fieldName,
    options,
    updateValue,
    onChange,
    other,
    otherValue,
    otherFieldLabel,
    displayOtherField,
    isCorrection,
    answer
  } = props;

  const otherField = useMemo(
    () => (displayOtherField ? { label: otherFieldLabel, value: "" } : null),
    [displayOtherField, otherFieldLabel]
  );

  const toSelectOption = useCallback(
    option => ({
      value: option.id,
      label: isCorrection ? quizzAnswerLabel(option) : option.name,
      isCorrect: option.isCorrect,
      correctionText: option.correctionText
    }),
    [isCorrection]
  );

  const selectOptions = useMemo(
    () => options.map(toSelectOption),
    [options, toSelectOption]
  );

  // Only this input knows the option the form store kept when one was picked, which is
  // what labels a value chosen before the answer is saved.
  const storedOptions = useMemo(
    () => (props.value?.label ? [props.value] : []),
    [props.value]
  );

  const selectedValues = useMemo(() => {
    const selectedValue = props.value?.value;

    if (selectedValue) return [selectedValue];

    return other ? [""] : [];
  }, [props.value?.value, other]);

  const update = useCallback(
    data => {
      const currentData = { value: props.value, other, otherValue };

      updateValue({ ...currentData, ...data });
    },
    [props.value, other, otherValue, updateValue]
  );

  const handleOptionsChanged = useCallback(
    ([option = null]) => {
      const other = option?.value === "";
      const newOtherValue = other ? otherValue : "";

      update({ value: option, other, otherValue: newOtherValue });

      if (onChange) onChange(option);
    },
    [otherValue, update, onChange]
  );

  const handleOtherChanged = useCallback(data => update(data), [update]);

  const passthroughProps = omit(props, [
    "defaultValue",
    "value",
    "options",
    "updateValue",
    "fieldName",
    "formKey",
    "onChange",
    "answer"
  ]);

  return (
    <>
      <FormFieldOptionsSelect
        inputId={fieldId}
        className="react-select"
        classNamePrefix="react-select"
        name={fieldName}
        answer={answer}
        options={selectOptions}
        storedOptions={storedOptions}
        otherField={otherField}
        selectedValues={selectedValues}
        onOptionsChanged={handleOptionsChanged}
        isClearable
        styles={{ menu: provided => ({ ...provided, zIndex: 2000 }) }}
        {...passthroughProps}
      />

      <OtherFieldForSelect
        otherFieldLabel={otherFieldLabel}
        other={other}
        otherValue={otherValue}
        onChange={handleOtherChanged}
      />
    </>
  );
};

export default inputConnect(CheckboxesSelectInput, {
  fields: { value: null, other: false, otherValue: "" }
});
